import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CircularProgress, Box, Typography, Alert } from '@mui/material';
// import { doc, getDoc, collection, getDocs } from 'firebase-shim';
// import { db } from '../firebase/config';
import * as indexedDBService from '../utils/indexedDBService';
import { useAuth } from '../context/AuthContext';
import CreateInvoiceDialog from '../components/dialogs/CreateInvoiceDialog';

const CreateInvoice: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invoice, setInvoice] = useState<any>(null);
  const [clients, setClients] = useState<any[]>([]);
  const [inventory, setInventory] = useState<any[]>([]);
  const [businessDetails, setBusinessDetails] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      // For new invoice creation, redirect to the new page
      if (!id) {
        navigate('/invoices/new', { replace: true });
        return;
      }

      if (!currentUser) {
        setError('Please log in to edit invoices');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Fetch the invoice
        const invoiceData = await indexedDBService.getItemById('invoices', id);

        if (!invoiceData) {
          setError('Invoice not found');
          setLoading(false);
          return;
        }

        setInvoice(invoiceData);

        // Fetch clients
        const clientsData = await indexedDBService.getAllItems<any>('clients');
        setClients(clientsData || []);

        // Fetch inventory
        const inventoryData = await indexedDBService.getAllItems<any>('inventory');
        setInventory(inventoryData || []);

        // Fetch business details
        // Try getting from business service or user profile
        // In this app structure, we'll try to get the first business for the user
        const businesses = await indexedDBService.getAllItems<any>('businesses');
        const userBusiness = businesses.find(b => b.ownerId === currentUser.uid);
        if (userBusiness) {
          setBusinessDetails(userBusiness);
        } else {
          // Fallback to user profile business data if available
          const profile = await indexedDBService.getUserProfile(currentUser.uid);
          if (profile) {
            setBusinessDetails(profile);
          }
        }

        setLoading(false);
      } catch (error) {
        console.error('Error loading invoice data:', error);
        setError('An error occurred while loading the invoice data');
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate, currentUser]);

  const handleClose = () => {
    navigate('/invoices');
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '70vh'
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading invoice editor...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '70vh',
          p: 3
        }}
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="body1">
          Please try again or go back to invoices.
        </Typography>
      </Box>
    );
  }

  if (invoice) {
    return (
      <CreateInvoiceDialog
        open={true}
        onClose={handleClose}
        clients={clients}
        inventory={inventory}
        businessDetails={businessDetails}
        editMode={true}
        initialInvoiceData={invoice}
      />
    );
  }

  return null;
};

export default CreateInvoice; 
